/**
 * IUROP Sidecart JavaScript
 * Handles: Upsells carousel, Variant popup, Timer, Cart updates
 */

(function() {
  'use strict';

  // Upsells Carousel
  class UpsellsCarousel {
    constructor(container) {
      this.container = container;
      if (!this.container) return;

      this.carousel = this.container.querySelector('[data-upsell-carousel]');
      this.wrapper = this.container.querySelector('.iurop-sidecart-upsells__carousel-wrapper');
      this.prevBtn = this.container.querySelector('[data-upsell-prev]');
      this.nextBtn = this.container.querySelector('[data-upsell-next]');
      this.cards = [];

      if (!this.carousel || !this.wrapper) return;

      this.currentIndex = this.getStoredIndex();
      this.cardWidth = 0;
      this.cardGap = 0;
      this.visibleCards = 1;
      this.maxIndex = 0;
      this.handlePrevClick = this.prev.bind(this);
      this.handleNextClick = this.next.bind(this);
      this.handleResize = this.handleResize.bind(this);

      this.init();
    }

    getCardWidth() {
      const firstCard = this.cards[0];
      if (!firstCard) return 0;

      const cardWidth = firstCard.getBoundingClientRect().width;
      const carouselStyles = window.getComputedStyle(this.carousel);
      const gap = parseFloat(carouselStyles.columnGap || carouselStyles.gap || '0');

      this.cardGap = Number.isFinite(gap) ? gap : 0;
      return cardWidth;
    }

    getStoredIndex() {
      const storedIndex = parseInt(this.container.dataset.upsellIndex || '0', 10);
      return Number.isFinite(storedIndex) && storedIndex > 0 ? storedIndex : 0;
    }

    setCurrentIndex(index) {
      this.currentIndex = Math.max(0, Math.min(this.maxIndex, index));
      this.container.dataset.upsellIndex = String(this.currentIndex);
    }

    getVisibleCards() {
      if (!this.cardWidth) return 1;

      const wrapperWidth = this.wrapper.getBoundingClientRect().width;
      const trackItemWidth = this.cardWidth + this.cardGap;

      if (!trackItemWidth) return 1;

      return Math.max(1, Math.floor((wrapperWidth + this.cardGap + 1) / trackItemWidth));
    }

    calculateMetrics() {
      this.resetCardVisibility();
      this.cardWidth = this.getCardWidth();
      this.visibleCards = this.getVisibleCards();
      this.maxIndex = Math.max(0, this.cards.length - this.visibleCards);

      this.setCurrentIndex(this.currentIndex);

      this.render();
      this.updateButtons();
    }

    refresh() {
      this.cards = Array.from(this.carousel.querySelectorAll('.iurop-sidecart-upsells__card'));
      this.calculateMetrics();
    }

    init() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', this.handlePrevClick);
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', this.handleNextClick);
      }

      this.refresh();
      window.addEventListener('resize', this.handleResize);
    }

    handleResize() {
      this.refresh();
    }

    destroy() {
      if (this.prevBtn) {
        this.prevBtn.removeEventListener('click', this.handlePrevClick);
      }
      if (this.nextBtn) {
        this.nextBtn.removeEventListener('click', this.handleNextClick);
      }
      window.removeEventListener('resize', this.handleResize);
      this.resetCardVisibility();
    }

    prev() {
      if (this.currentIndex > 0) {
        this.setCurrentIndex(this.currentIndex - 1);
        this.render();
        this.updateButtons();
      }
    }

    next() {
      if (this.currentIndex < this.maxIndex) {
        this.setCurrentIndex(this.currentIndex + 1);
        this.render();
        this.updateButtons();
      }
    }

    resetCardVisibility() {
      if (!this.carousel) return;

      this.carousel.style.transform = 'translate3d(0, 0, 0)';
      this.cards.forEach((card) => {
        card.hidden = false;
        card.style.display = '';
        card.setAttribute('aria-hidden', 'false');
      });
    }

    render() {
      if (!this.carousel) return;

      if (!this.cards.length) {
        this.carousel.style.transform = 'translate3d(0, 0, 0)';
        return;
      }

      const start = this.currentIndex;
      const end = start + this.visibleCards;

      this.carousel.style.transform = 'translate3d(0, 0, 0)';
      this.cards.forEach((card, index) => {
        const isVisible = index >= start && index < end;
        card.hidden = !isVisible;
        card.style.display = isVisible ? '' : 'none';
        card.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
      });
    }

    updateButtons() {
      const shouldShowControls = this.maxIndex > 0;

      if (this.prevBtn) {
        this.prevBtn.style.display = shouldShowControls ? '' : 'none';
        this.prevBtn.disabled = this.currentIndex === 0;
        const prevSvg = this.prevBtn.querySelector('path');
        if (prevSvg) {
          prevSvg.setAttribute('opacity', this.currentIndex === 0 ? '0.5' : '1');
        }
      }
      if (this.nextBtn) {
        this.nextBtn.style.display = shouldShowControls ? '' : 'none';
        this.nextBtn.disabled = this.currentIndex >= this.maxIndex;
        const nextSvg = this.nextBtn.querySelector('path');
        if (nextSvg) {
          nextSvg.setAttribute('opacity', this.currentIndex >= this.maxIndex ? '0.5' : '1');
        }
      }
    }
  }

  function getSidecartUpsellsContainer() {
    return document.querySelector('.iurop-header__cart-drawer .cart-drawer__dialog[open] [data-sidecart-upsells]')
      || document.querySelector('.iurop-header__cart-drawer [data-sidecart-upsells]')
      || document.querySelector('[data-sidecart-upsells]');
  }

  function fetchJsonWithTimeout(url, timeoutMs) {
    return Promise.race([
      fetch(url),
      new Promise(function(_, reject) {
        setTimeout(function() {
          reject(new Error('Upsells request timeout'));
        }, timeoutMs || 8000);
      })
    ]).then(function(response) {
      if (!response.ok) {
        throw new Error('Upsells request failed');
      }
      return response.json();
    });
  }

  function formatUpsellMoney(cents) {
    return (cents / 100).toFixed(2).replace('.', ',') + '€';
  }

  function bindUpsellButtons(container, carousel) {
    carousel.querySelectorAll('[data-upsell-add]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        const originalText = btn.textContent;

        if (typeof window.iuropAddToCart === 'function') {
          window.iuropAddToCart({
            variantId: String(this.dataset.variantId),
            quantity: 1,
            button: btn,
            sourceId: 'iurop-upsell',
            openSidecartAfter: false,
            busyText: 'Añadiendo...',
            successText: '¡Añadido!'
          }).catch(function(err) {
            console.error('Error adding to cart:', err);
            btn.textContent = originalText;
            btn.disabled = false;
          });
          return;
        }

        btn.disabled = true;
        btn.textContent = 'Añadiendo...';
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: String(btn.dataset.variantId), quantity: 1 })
        })
          .then(function(response) { return response.json(); })
          .then(function() {
            btn.textContent = '¡Añadido!';
            setTimeout(function() {
              btn.textContent = originalText;
              btn.disabled = false;
            }, 1500);

            return fetch('/cart.js')
              .then(function(r) { return r.json(); })
              .then(function(cart) {
                document.dispatchEvent(new CustomEvent('cart:update', {
                  bubbles: true,
                  detail: {
                    resource: cart,
                    sourceId: 'iurop-upsell',
                    data: { source: 'iurop-upsell', itemCount: cart.item_count || 0 }
                  }
                }));
              });
          })
          .catch(function(err) {
            console.error('Error adding to cart:', err);
            btn.textContent = originalText;
            btn.disabled = false;
          });
      });
    });

    carousel.querySelectorAll('[data-upsell-select-variant]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        if (window.iuropVariantPopup) {
          window.iuropVariantPopup.open(this.dataset.productHandle);
        }
      });
    });

    if (window.__iuropUpsellsInstance && typeof window.__iuropUpsellsInstance.refresh === 'function') {
      window.__iuropUpsellsInstance.refresh();
    } else if (typeof window.iuropInitSidecart === 'function') {
      window.iuropInitSidecart();
    }
  }

  function renderSidecartUpsells(container, products) {
    const carousel = container.querySelector('[data-upsell-carousel]');
    if (!carousel) return;

    if (!products || products.length === 0) {
      container.style.display = 'none';
      return;
    }

    const buttonText = container.dataset.buttonText || 'Añadir';

    carousel.innerHTML = products.map(function(product) {
      const hasVariants = product.variants && product.variants.length > 1;
      const firstVariant = product.variants && product.variants[0];
      const isAvailable = product.available;
      const hasComparePrice = product.compare_at_price && product.compare_at_price > product.price;
      let featuredImage = '';

      if (typeof product.featured_image === 'string') {
        featuredImage = product.featured_image;
      } else if (product.featured_image && product.featured_image.url) {
        featuredImage = product.featured_image.url;
      }

      if (!firstVariant) return '';

      return '<div class="iurop-sidecart-upsells__card" data-product-id="' + product.id + '">'
        + '<a href="' + product.url + '" class="iurop-sidecart-upsells__card-image">'
        + (featuredImage
          ? '<img src="' + featuredImage.replace(/\.([^.]+)$/, '_200x.$1') + '" alt="' + product.title + '" loading="lazy">'
          : '<div class="placeholder-svg"></div>')
        + '</a>'
        + '<div class="iurop-sidecart-upsells__card-content">'
        + '<a href="' + product.url + '" class="iurop-sidecart-upsells__card-title">' + product.title + '</a>'
        + '<div class="iurop-sidecart-upsells__card-prices">'
        + '<span class="iurop-sidecart-upsells__card-price">' + formatUpsellMoney(product.price) + '</span>'
        + (hasComparePrice ? '<span class="iurop-sidecart-upsells__card-compare-price">' + formatUpsellMoney(product.compare_at_price) + '</span>' : '')
        + '</div>'
        + (hasVariants
          ? '<button type="button" class="iurop-sidecart-upsells__card-button" data-upsell-select-variant data-product-handle="' + product.handle + '" data-product-id="' + product.id + '" ' + (!isAvailable ? 'disabled' : '') + '>' + buttonText + '</button>'
          : '<button type="button" class="iurop-sidecart-upsells__card-button" data-upsell-add data-variant-id="' + firstVariant.id + '" ' + (!isAvailable ? 'disabled' : '') + '>' + buttonText + '</button>')
        + '</div></div>';
    }).join('');

    if (carousel.querySelectorAll('.iurop-sidecart-upsells__card').length === 0) {
      container.style.display = 'none';
      return;
    }

    container.style.display = '';
    bindUpsellButtons(container, carousel);
  }

  function loadSidecartUpsells() {
    const container = getSidecartUpsellsContainer();
    if (!container) return Promise.resolve();

    const existingCards = container.querySelectorAll('.iurop-sidecart-upsells__card').length;
    if (container.dataset.loaded === 'true' && existingCards > 0) return Promise.resolve();
    if (container.dataset.loading === 'true') return Promise.resolve();
    if (container.dataset.loaded === 'true' && existingCards === 0) {
      container.dataset.loaded = '';
    }

    const baseProductId = container.dataset.baseProductId;
    const cartProductIdsStr = container.dataset.cartProductIds || '';
    const cartProductIds = cartProductIdsStr ? cartProductIdsStr.split(',').map(function(id) { return parseInt(id, 10); }) : [];
    const limit = parseInt(container.dataset.limit, 10) || 6;
    const carousel = container.querySelector('[data-upsell-carousel]');

    if (!baseProductId || !carousel) return Promise.resolve();

    container.dataset.loaded = 'true';
    container.dataset.loading = 'true';

    return fetchJsonWithTimeout('/recommendations/products.json?product_id=' + baseProductId + '&limit=' + (limit + cartProductIds.length) + '&intent=complementary', 8000)
      .then(function(data) {
        const recommendations = data.products
          .filter(function(product) { return cartProductIds.indexOf(product.id) === -1; })
          .slice(0, limit);

        if (recommendations.length > 0) return recommendations;

        return fetchJsonWithTimeout('/recommendations/products.json?product_id=' + baseProductId + '&limit=' + (limit + cartProductIds.length) + '&intent=related', 8000)
          .then(function(relatedData) {
            return relatedData.products
              .filter(function(product) { return cartProductIds.indexOf(product.id) === -1; })
              .slice(0, limit);
          });
      })
      .then(function(products) {
        container.dataset.loading = 'false';
        renderSidecartUpsells(container, products);
      })
      .catch(function(error) {
        console.error('Error loading recommendations:', error);
        container.dataset.loading = 'false';
        container.style.display = 'none';
      });
  }

  window.iuropLoadUpsells = loadSidecartUpsells;

  // Variant Popup
  class VariantPopup {
    constructor() {
      this.popup = document.getElementById('iurop-variant-popup');
      if (!this.popup) return;

      this.closeBtns = this.popup.querySelectorAll('[data-popup-close]');
      this.addBtn = this.popup.querySelector('[data-popup-add]');
      this.imageEl = this.popup.querySelector('[data-popup-image]');
      this.titleEl = this.popup.querySelector('[data-popup-title]');
      this.priceEl = this.popup.querySelector('[data-popup-price]');
      this.comparePriceEl = this.popup.querySelector('[data-popup-compare-price]');
      this.optionsContainer = this.popup.querySelector('[data-popup-options]');

      this.productData = null;
      this.selectedOptions = {};
      this.originalAddText = this.addBtn ? this.addBtn.textContent.trim() : 'Añadir al carrito';

      this.init();
    }

    init() {
      // Close popup (X button, overlay, close button)
      this.closeBtns.forEach(btn => {
        btn.addEventListener('click', () => this.close());
      });

      // Close on Escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.popup.classList.contains('is-open')) {
          this.close();
        }
      });

      // Add to cart
      if (this.addBtn) {
        this.addBtn.addEventListener('click', () => this.addToCart());
      }
    }

    async open(productHandle) {
      try {
        // Close sidecart if open
        this.closeSidecart();

        const response = await fetch(`/products/${productHandle}.js`);
        if (!response.ok) throw new Error(`Failed to load product ${productHandle}`);
        this.productData = await response.json();

        this.selectedOptions = {};
        this.render();
        this.popup.classList.add('is-open');
        this.popup.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
      } catch (error) {
        console.error('Error loading product:', error);
      }
    }

    closeSidecart() {
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer) {
        const dialog = cartDrawer.querySelector('dialog');
        if (dialog && dialog.hasAttribute('open')) {
          dialog.close();
        }
      }
    }

    openSidecart() {
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer && cartDrawer.showDialog) {
        cartDrawer.showDialog();
      } else if (cartDrawer) {
        const dialog = cartDrawer.querySelector('dialog');
        if (dialog) dialog.showModal();
      }
    }

    close() {
      this.popup.classList.remove('is-open');
      this.popup.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      this.productData = null;
      this.selectedOptions = {};
    }

    // Determine if an option should render as swatches (has variant images)
    optionHasImages(optionIndex) {
      const imagesByValue = {};
      this.productData.variants.forEach(v => {
        const val = v.options[optionIndex];
        if (v.featured_image && v.featured_image.src) {
          imagesByValue[val] = v.featured_image.src;
        }
      });
      return Object.keys(imagesByValue).length >= 2 ? imagesByValue : null;
    }

    render() {
      if (!this.productData) return;

      // Update product image
      if (this.imageEl && this.productData.featured_image) {
        this.imageEl.src = this.getSizedImageUrl(this.productData.featured_image, '600x');
        this.imageEl.alt = this.productData.title;
      }

      // Update title
      if (this.titleEl) {
        this.titleEl.textContent = this.productData.title;
      }

      // Update prices
      this.updatePrices(this.productData.price, this.productData.compare_at_price);

      // Render options
      if (this.optionsContainer) {
        this.optionsContainer.innerHTML = '';

        this.productData.options.forEach((option, index) => {
          const optionName = this.getOptionName(option, index);
          const optionValues = [...new Set(this.productData.variants.map(v => v.options[index]))];
          const imageMap = this.optionHasImages(index);

          const optionGroup = document.createElement('div');
          optionGroup.className = 'iurop-variant-popup__option-group';

          if (imageMap) {
            // Render as swatches with images
            optionGroup.innerHTML = `
              <span class="iurop-variant-popup__option-label">${optionName}: <span data-option-selected="${index}"></span></span>
              <div class="iurop-variant-popup__option-swatches" data-option-index="${index}">
                ${optionValues.map(value => {
                  const imgSrc = imageMap[value] || this.productData.featured_image;
                  return `
                    <button type="button" class="iurop-variant-popup__swatch" data-option-value="${value}">
                      <div class="iurop-variant-popup__swatch-image">
                        <img src="${this.getSizedImageUrl(imgSrc, '120x')}" alt="${value}" loading="lazy">
                      </div>
                      <span class="iurop-variant-popup__swatch-label">${value}</span>
                    </button>
                  `;
                }).join('')}
              </div>
            `;

            optionGroup.querySelectorAll('.iurop-variant-popup__swatch').forEach(btn => {
              btn.addEventListener('click', () => this.selectOption(index, btn.dataset.optionValue));
            });
          } else {
            // Render as dropdown
            optionGroup.innerHTML = `
              <span class="iurop-variant-popup__option-label">${optionName}: <span data-option-selected="${index}"></span></span>
              <select class="iurop-variant-popup__option-select" data-option-index="${index}">
                <option value="">${optionName}</option>
                ${optionValues.map(value => `
                  <option value="${value}">${value}</option>
                `).join('')}
              </select>
            `;

            const select = optionGroup.querySelector('select');
            select.addEventListener('change', () => {
              if (select.value) {
                this.selectOption(index, select.value);
              }
            });
          }

          this.optionsContainer.appendChild(optionGroup);
        });
      }

      this.updateAddButton();
    }

    selectOption(index, value) {
      this.selectedOptions[index] = value;

      // Update selected label
      const selectedLabel = this.optionsContainer.querySelector(`[data-option-selected="${index}"]`);
      if (selectedLabel) {
        selectedLabel.textContent = value;
      }

      // Update UI for swatches
      const swatchContainer = this.optionsContainer.querySelector(`.iurop-variant-popup__option-swatches[data-option-index="${index}"]`);
      if (swatchContainer) {
        swatchContainer.querySelectorAll('.iurop-variant-popup__swatch').forEach(btn => {
          btn.classList.toggle('is-selected', btn.dataset.optionValue === value);
        });
      }

      // Update UI for dropdowns
      const selectEl = this.optionsContainer.querySelector(`select[data-option-index="${index}"]`);
      if (selectEl) {
        selectEl.value = value;
      }

      // Update main image based on selected variant
      const variant = this.getSelectedVariant();
      if (variant && variant.featured_image && this.imageEl) {
        this.imageEl.src = this.getSizedImageUrl(variant.featured_image.src, '600x');
      } else {
        // Try to find an image from partial match
        const partialVariant = this.findVariant(this.selectedOptions);
        if (partialVariant && partialVariant.featured_image && this.imageEl) {
          this.imageEl.src = this.getSizedImageUrl(partialVariant.featured_image.src, '600x');
        }
      }

      // Check availability of other options
      this.updateOptionAvailability();
      this.updateAddButton();
    }

    updateOptionAvailability() {
      this.productData.options.forEach((option, index) => {
        // For swatches
        const swatchContainer = this.optionsContainer.querySelector(`.iurop-variant-popup__option-swatches[data-option-index="${index}"]`);
        if (swatchContainer) {
          swatchContainer.querySelectorAll('.iurop-variant-popup__swatch').forEach(btn => {
            const testOptions = { ...this.selectedOptions, [index]: btn.dataset.optionValue };
            const variant = this.findVariant(testOptions);
            btn.classList.toggle('is-unavailable', !variant || !variant.available);
          });
        }

        // For dropdowns
        const selectEl = this.optionsContainer.querySelector(`select[data-option-index="${index}"]`);
        if (selectEl) {
          selectEl.querySelectorAll('option[value]').forEach(opt => {
            if (!opt.value) return;
            const testOptions = { ...this.selectedOptions, [index]: opt.value };
            const variant = this.findVariant(testOptions);
            opt.disabled = !variant || !variant.available;
          });
        }
      });
    }

    findVariant(options) {
      const optionKeys = Object.keys(options).map(Number).sort((a, b) => a - b);

      return this.productData.variants.find(variant => {
        return optionKeys.every(key => variant.options[key] === options[key]);
      });
    }

    getSelectedVariant() {
      if (Object.keys(this.selectedOptions).length !== this.productData.options.length) {
        return null;
      }
      return this.findVariant(this.selectedOptions);
    }

    updatePrices(price, compareAtPrice) {
      if (this.priceEl) {
        this.priceEl.textContent = this.formatMoney(price);
      }
      if (this.comparePriceEl) {
        if (compareAtPrice && compareAtPrice > price) {
          this.comparePriceEl.textContent = this.formatMoney(compareAtPrice);
        } else {
          this.comparePriceEl.textContent = '';
        }
      }
    }

    updateAddButton() {
      const variant = this.getSelectedVariant();
      if (this.addBtn) {
        this.addBtn.disabled = !variant || !variant.available;
        if (variant && !variant.available) {
          this.addBtn.textContent = 'Agotado';
        } else {
          this.addBtn.textContent = this.originalAddText;
        }
      }

      // Update prices based on selected variant
      if (variant) {
        this.updatePrices(variant.price, variant.compare_at_price);
      }
    }

    async addToCart() {
      const variant = this.getSelectedVariant();
      if (!variant) return;

      try {
        const addToCart = typeof window.iuropAddToCart === 'function'
          ? window.iuropAddToCart
          : addToCartAndSync;

        const result = await addToCart({
          variantId: String(variant.id),
          quantity: 1,
          button: this.addBtn,
          sourceId: 'iurop-variant-popup',
          openSidecartAfter: true,
          busyText: 'Añadiendo...',
          successText: '¡Añadido!'
        });

        if (result && result.ok) {
          this.close();
        } else if (this.addBtn) {
          this.addBtn.disabled = false;
        }
      } catch (error) {
        console.error('Error adding to cart:', error);
        if (this.addBtn) {
          this.addBtn.textContent = this.originalAddText;
          this.addBtn.disabled = false;
        }
      }
    }

    getOptionName(option, index) {
      if (typeof option === 'string' && option.trim()) return option;
      if (option && typeof option === 'object') {
        if (typeof option.name === 'string' && option.name.trim()) return option.name;
        if (typeof option.title === 'string' && option.title.trim()) return option.title;
      }
      return `Opción ${index + 1}`;
    }

    getImageUrl(image) {
      if (!image) return '';
      if (typeof image === 'string') return image;
      if (typeof image.src === 'string') return image.src;
      if (typeof image.url === 'string') return image.url;
      return '';
    }

    getSizedImageUrl(image, size) {
      const url = this.getImageUrl(image);
      if (!url) return '';
      return url.replace(/(\.[^.?#]+)(\?.*)?$/, `_${size}$1$2`);
    }

    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2).replace('.', ',');
      return `${amount}€`;
    }
  }

  // Cart Timer (singleton - only one instance at a time)
  let activeTimerInterval = null;

  class CartTimer {
    constructor(container) {
      this.container = container;
      if (!this.container) return;

      // Prevent duplicate timers
      if (this.container.dataset.timerInitialized === 'true') return;
      this.container.dataset.timerInitialized = 'true';

      // Clear any previous interval
      if (activeTimerInterval) {
        clearInterval(activeTimerInterval);
        activeTimerInterval = null;
      }

      this.display = this.container.querySelector('[data-timer-display]');
      this.minutes = parseInt(this.container.dataset.minutes) || 10;
      this.seconds = this.minutes * 60;

      this.init();
    }

    init() {
      // Check if timer was started before
      const savedTime = sessionStorage.getItem('iurop_cart_timer');
      const savedTimestamp = sessionStorage.getItem('iurop_cart_timer_start');

      if (savedTime && savedTimestamp) {
        const elapsed = Math.floor((Date.now() - parseInt(savedTimestamp)) / 1000);
        this.seconds = Math.max(0, parseInt(savedTime) - elapsed);
      } else {
        sessionStorage.setItem('iurop_cart_timer', this.seconds);
        sessionStorage.setItem('iurop_cart_timer_start', Date.now());
      }

      this.update();
      this.interval = setInterval(() => this.tick(), 1000);
      activeTimerInterval = this.interval;
    }

    tick() {
      if (this.seconds > 0) {
        this.seconds--;
        this.update();
      } else {
        clearInterval(this.interval);
        this.onExpired();
      }
    }

    update() {
      if (!this.display) return;

      const mins = Math.floor(this.seconds / 60);
      const secs = this.seconds % 60;
      this.display.textContent = `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }

    onExpired() {
      if (!this.container) return;
      // Replace the timer content with an expired message
      const textEl = this.container.querySelector('span:not([data-timer-display])');
      if (textEl) textEl.textContent = '¡Tu reserva ha expirado!';
      if (this.display) this.display.style.display = 'none';
      // Reset session so next cart open starts a fresh timer
      sessionStorage.removeItem('iurop_cart_timer');
      sessionStorage.removeItem('iurop_cart_timer_start');
    }
  }

  // Quick Add to Cart
  async function handleQuickAdd(variantId) {
    if (!variantId) return;

    try {
      await addToCartAndSync({
        variantId: String(variantId),
        quantity: 1,
        sourceId: 'iurop-upsell',
        openSidecartAfter: false
      });
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  }

  async function refreshCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer) {
      isRefreshingDrawer = true;
      if (refreshDrawerReleaseTimer) {
        clearTimeout(refreshDrawerReleaseTimer);
      }
      refreshDrawerReleaseTimer = setTimeout(function () {
        isRefreshingDrawer = false;
        refreshDrawerReleaseTimer = null;
      }, 500);

      // Dispatch custom event to trigger Horizon's cart update
      document.dispatchEvent(new CustomEvent('cart:refresh'));

      // Also try to use Horizon's built-in refresh
      const sectionId = document.querySelector('.cart-items-component')?.dataset?.sectionId;
      if (sectionId) {
        try {
          const response = await fetch(`${window.location.pathname}?section_id=${sectionId}`);
          const html = await response.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const newContent = doc.querySelector('.cart-items-component');
          const currentContent = document.querySelector('.cart-items-component');
          if (newContent && currentContent) {
            currentContent.innerHTML = newContent.innerHTML;
            initSidecart();
          }
        } catch (e) {
          window.location.reload();
        }
      }
    }
  }

  let isSyncingCart = false;
  let cartSyncScheduled = false;
  let shouldOpenSidecart = false;
  let isRefreshingDrawer = false;
  let refreshDrawerReleaseTimer = null;

  function getCartEventSource(event) {
    const detail = event && event.detail ? event.detail : null;
    if (!detail) return null;

    return detail.data && detail.data.source
      ? detail.data.source
      : detail.sourceId || detail.source;
  }

  function shouldAutoOpen(source) {
    return (
      source === 'product-form-component' ||
      source === 'iurop-ajax-atc' ||
      source === 'iurop-product-card' ||
      source === 'iurop-product-list' ||
      source === 'iurop-combos' ||
      source === 'iurop-upsell' ||
      source === 'iurop-variant-popup'
    );
  }

  function parseCartQuantity(value, fallback) {
    var quantity = parseInt(value, 10);
    if (!Number.isFinite(quantity) || quantity < 1) return fallback;
    return quantity;
  }

  function dispatchCartUpdate(cartData, sourceId) {
    var itemCount = cartData && typeof cartData.item_count !== 'undefined'
      ? cartData.item_count
      : 0;

    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cartData || {},
        sourceId: sourceId,
        data: {
          source: sourceId,
          itemCount: itemCount
        }
      }
    }));
  }

  async function addToCartAndSync(params) {
    var options = params || {};
    var variantId = options.variantId;
    var button = options.button;
    var sourceId = options.sourceId || 'iurop-ajax-atc';
    var quantity = parseCartQuantity(options.quantity, 1);
    var openSidecartAfter = options.openSidecartAfter || false;
    var busyText = options.busyText || 'Añadiendo...';
    var successText = options.successText || '¡Añadido!';

    if (!variantId) return { ok: false, error: new Error('Missing variant id') };

    var originalText = button && typeof button.textContent === 'string'
      ? button.textContent.trim()
      : '';

    if (button) {
      button.disabled = true;
      button.textContent = busyText;
    }

    try {
      var response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: String(variantId),
          quantity: quantity
        })
      });

      var addData = await response.json().catch(function () { return {}; });
      if (!response.ok || (addData && addData.status)) {
        throw new Error((addData && (addData.message || addData.description)) || 'Error al añadir al carrito');
      }

      if (button) {
        button.textContent = successText;
      }

      var cart = {};
      try {
        var cartRes = await fetch('/cart.js');
        cart = await cartRes.json();
      } catch (cartError) {
        cart = {};
      }

      dispatchCartUpdate(cart, sourceId);

      if (openSidecartAfter && !window.__iuropCrossSellPending) {
        setTimeout(function () {
          openSidecart();
        }, 300);
      }

      return { ok: true, cart: cart, data: addData };
    } catch (error) {
      console.error('IUROP: add-to-cart failed', error);

      if (button) {
        button.textContent = 'Error';
      }

      return { ok: false, error: error };
    } finally {
      if (button) {
        setTimeout(function () {
          button.disabled = false;
          button.textContent = originalText || 'Añadir al carrito';
        }, 1500);
      }
    }
  }

  if (typeof window.iuropAddToCart !== 'function') {
    window.iuropAddToCart = addToCartAndSync;
  }

  async function syncCartFromServer() {
    if (isSyncingCart) {
      cartSyncScheduled = true;
      return;
    }

    isSyncingCart = true;

    try {
      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();
      const itemCount = cart && typeof cart.item_count !== 'undefined' ? cart.item_count : 0;

      document.querySelectorAll('.cart-bubble').forEach(function(bubble) {
        bubble.textContent = String(itemCount);
      });

      try {
        await refreshCartDrawer();
      } catch (e) {
        console.error('IUROP: Failed to refresh sidecart', e);
      }
    } catch (e) {
      console.error('IUROP: Failed to sync cart state from server', e);
    } finally {
      isSyncingCart = false;

      if (cartSyncScheduled) {
        cartSyncScheduled = false;
        syncCartFromServer();
      }
    }
  }

  function scheduleCartSync(openSidecartOnComplete) {
    if (openSidecartOnComplete) {
      shouldOpenSidecart = true;
    }

    if (isSyncingCart || cartSyncScheduled) return;

    cartSyncScheduled = true;
    Promise.resolve().then(function() {
      cartSyncScheduled = false;

      syncCartFromServer().then(function() {
        if (shouldOpenSidecart && !window.__iuropCrossSellPending) {
          setTimeout(function() {
            openSidecart();
          }, 300);
        }
        shouldOpenSidecart = false;
      });
    });
  }

  // Initialize all sidecart components
  function initSidecart() {
    // Initialize upsells carousel
    const upsellsContainer = document.querySelector('.iurop-header__cart-drawer .cart-drawer__dialog[open] [data-sidecart-upsells]')
      || document.querySelector('.iurop-header__cart-drawer [data-sidecart-upsells]')
      || document.querySelector('[data-sidecart-upsells]');
    if (window.__iuropUpsellsInstance && typeof window.__iuropUpsellsInstance.destroy === 'function') {
      window.__iuropUpsellsInstance.destroy();
      window.__iuropUpsellsInstance = null;
    }
    if (upsellsContainer) {
      window.__iuropUpsellsInstance = new UpsellsCarousel(upsellsContainer);
    }

    // Initialize timer
    const timerContainer = document.querySelector('[data-sidecart-timer]');
    if (timerContainer) {
      new CartTimer(timerContainer);
    }

    if (typeof window.iuropLoadUpsells === 'function') {
      window.iuropLoadUpsells();
    }

  }

  window.iuropInitSidecart = initSidecart;

  // Expose refreshCartDrawer globally for inline scripts (e.g., gift auto-add)
  window.refreshCartDrawer = refreshCartDrawer;

  // Expose reinit function for variant popup (called after DOM changes)
  window.iuropInitVariantPopup = function() {
    window.iuropVariantPopup = new VariantPopup();
  };

  // Open sidecart helper (reusable)
  function openSidecart() {
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer && cartDrawer.showDialog) {
      cartDrawer.showDialog();
    } else if (cartDrawer) {
      const dialog = cartDrawer.querySelector('dialog');
      if (dialog) dialog.showModal();
    }
  }

  async function submitProductAddForm(form, sourceId, e) {
    if (!form || e.defaultPrevented) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    if (form.dataset.openVariantPopup === 'true' && form.dataset.productHandle && window.iuropVariantPopup) {
      window.iuropVariantPopup.open(form.dataset.productHandle);
      return;
    }

    const variantInput = form.querySelector('input[name="id"]');
    const quantityInput = form.querySelector('input[name="quantity"]');
    const button = form.querySelector('button[type="submit"]');
    if (!variantInput || !variantInput.value) return;

    const variantId = variantInput.value;
    const quantity = quantityInput ? quantityInput.value : 1;

    await window.iuropAddToCart({
      variantId: variantId,
      quantity: quantity,
      button: button,
      sourceId: sourceId,
      openSidecartAfter: false
    });
  }

  function bindProductAddForm(form, sourceId) {
    if (!form || form.dataset.iuropIntercepted) return;
    form.dataset.iuropIntercepted = 'true';

    form.addEventListener('submit', function(e) {
      submitProductAddForm(form, sourceId, e);
    });
  }

  // Intercept product card and combo forms (iurop-product-list__form, iurop-combos__form)
  function interceptProductCardForms() {
    document.querySelectorAll('.iurop-product-list__form').forEach(function(form) {
      bindProductAddForm(form, 'iurop-product-card');
    });

    document.querySelectorAll('.iurop-combos__form').forEach(function(form) {
      bindProductAddForm(form, 'iurop-combos');
    });
  }

  // Auto-open sidecart when cart:update fires from product page form
  function handleGlobalCartUpdate(e) {
    const source = getCartEventSource(e);
    scheduleCartSync(shouldAutoOpen(source));
  }

  function handleGlobalCartUpdated() {
    if (isRefreshingDrawer) return;
    scheduleCartSync(false);
  }

  // Update cart-drawer--empty class based on cart state after morph
  function updateCartEmptyState() {
    const cartItemsComponent = document.querySelector('cart-items-component');
    const dialog = document.querySelector('.cart-drawer__dialog');
    if (!cartItemsComponent || !dialog) return;

    const isEmpty = cartItemsComponent.getAttribute('data-cart-empty') === 'true';
    dialog.classList.toggle('cart-drawer--empty', isEmpty);
  }

  // Initialize on DOM ready
  document.addEventListener('DOMContentLoaded', function() {
    // Initialize variant popup (global)
    window.iuropInitVariantPopup();

    // Initialize sidecart components
    initSidecart();

    // Intercept product card add-to-cart forms
    interceptProductCardForms();

    // Keep intercepting newly injected forms after AJAX rendering
    document.addEventListener('submit', function(event) {
      const form = event.target.closest('.iurop-product-list__form, .iurop-combos__form');
      if (!form) return;

      const sourceId = form.classList.contains('iurop-combos__form') ? 'iurop-combos' : 'iurop-product-card';

      if (event.defaultPrevented) {
        return;
      }

      if (!form.dataset.iuropIntercepted) {
        form.dataset.iuropIntercepted = 'true';
        form.addEventListener('submit', function(e) {
          submitProductAddForm(form, sourceId, e);
        });
      }

      submitProductAddForm(form, sourceId, event);
    }, true);

    // Auto-open sidecart on product page add-to-cart
    document.addEventListener('cart:update', handleGlobalCartUpdate);

    // Listen for cart drawer open to reinitialize
    document.addEventListener('dialog:opened', initSidecart);

    // Listen for cart updates (Horizon event)
    document.addEventListener('cart:updated', handleGlobalCartUpdated);
  });

  // Observe cart-items-component for DOM changes (morph updates)
  const contentObserver = new MutationObserver(function() {
    updateCartEmptyState();
    setTimeout(initSidecart, 100);
  });

  // Also initialize when the dialog is shown
  const observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
        const dialog = mutation.target;
        if (dialog.hasAttribute('open')) {
          updateCartEmptyState();
          setTimeout(initSidecart, 100);
        }
      }
    });
  });

  // Observe cart drawer dialog and cart-items-component
  setTimeout(() => {
    const cartDialog = document.querySelector('.cart-drawer__dialog');
    if (cartDialog) {
      observer.observe(cartDialog, { attributes: true });
    }

    const cartItemsComponent = document.querySelector('cart-items-component');
    if (cartItemsComponent) {
      contentObserver.observe(cartItemsComponent, { childList: true, subtree: true });
    }
  }, 500);

})();
